import { useState } from "react";
import { format } from "date-fns";

import { api, errorMessage } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import StatusMessage, { errorStatus } from "../StatusMessage/StatusMessage";

/** Returns the post's full comment list after adding the new one. */
async function postComment(token, postGuid, content) {
  const data = await api.post("/family/api/new_comment/", token, {
    post_guid: postGuid,
    content,
  });
  return data.all_comments;
}

export function CommentForm({ postGuid, onCommentsChanged }) {
  const { session } = useAuth();
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setIsSubmitting(true);
    setMessage(null);
    try {
      onCommentsChanged(await postComment(session.token, postGuid, comment));
      setComment("");
    } catch (err) {
      setMessage(errorStatus(`Couldn't post the comment: ${errorMessage(err)}`));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="comment-form">
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Write a comment..."
        className="comment-input"
        disabled={isSubmitting}
      />
      <StatusMessage message={message} onDismiss={() => setMessage(null)} />
      <button type="submit" className="comment-submit" disabled={isSubmitting}>
        {isSubmitting ? "Posting..." : "Post Comment"}
      </button>
    </form>
  );
}

function EditCommentForm({ comment, onSaved, onCancel }) {
  const { session } = useAuth();
  const [content, setContent] = useState(comment.content);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setIsSaving(true);
    setMessage(null);
    try {
      onSaved(
        await api.patch(`/family/comments/${comment.comment_guid}/`, session.token, {
          content,
        }),
      );
    } catch (err) {
      setMessage(errorStatus(`Couldn't save the comment: ${errorMessage(err)}`));
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="comment-form">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="comment-input"
        aria-label="Edit comment"
        disabled={isSaving}
      />
      <StatusMessage message={message} onDismiss={() => setMessage(null)} />
      <div className="comment-edit-actions">
        <button type="submit" className="comment-submit" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </button>
        <button type="button" className="comment-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Comment({ comment, onUpdated }) {
  const { session } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const isMine = comment.member_guid_display === session?.member.member_guid;
  const tidyDate = format(Date.parse(comment.date_published), "MMM dd, yyyy");

  return (
    <div className="comment">
      <div className="comment-header">
        <span className="comment-author">
          {comment.member__first_name} {comment.member__last_name}
        </span>
        <span className="comment-date">{tidyDate}</span>
        {isMine && !isEditing && (
          <button
            type="button"
            className="comment-edit-button"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        )}
      </div>
      {isEditing ? (
        <EditCommentForm
          comment={comment}
          onSaved={(updated) => {
            onUpdated(updated);
            setIsEditing(false);
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        // plain text, as on the Django feed: comments aren't HTML
        <div className="comment-content">{comment.content}</div>
      )}
    </div>
  );
}

export function CommentList({ comments, onCommentsChanged }) {
  const replaceComment = (updated) =>
    onCommentsChanged(
      comments.map((c) => (c.comment_guid === updated.comment_guid ? updated : c)),
    );

  return (
    <div className="comments-section">
      {comments.length > 0 ? (
        comments.map((comment) => (
          <Comment
            key={comment.comment_guid}
            comment={comment}
            onUpdated={replaceComment}
          />
        ))
      ) : (
        <div className="comment-content"> No Comments, Leave a comment!</div>
      )}
    </div>
  );
}
